import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Calculator, Plus, Save, DollarSign, Download, Printer, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../providers/AppProvider';
import {
  AccountingMilestone,
  AccountingModule,
  AccountingRequirement,
  CapitalLedgerEntry,
  Project,
} from '../types';
import {
  setProjectAccountingMilestones,
  setProjectAccountingRequirements,
  setProjectCapitalLedgerEntries,
} from '../services/dataService';
import {
  REFERENCE_LEDGER_TRANSACTIONS,
  buildUnifiedAccountingCsv,
  computeUnifiedAccountingSnapshot,
  createUnifiedAccountingReportTemplate,
  validateUnifiedAccountingInputs,
} from '../utils/unifiedAccounting';
import { canEditProjectAccountingUi, canViewProjectAccountingUi } from '../utils/projectAccess';
import { generateId } from '../utils/idGenerator';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const moduleOptions: Array<{ value: AccountingModule; label: string }> = [
  { value: 'SR', label: 'Software Requirements (SR)' },
  { value: 'FCM', label: 'Financial/Construction Milestones (FCM)' },
];

const toDateValue = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toISOString().slice(0, 10);
};

const formatMoney = (value: number): string => `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const BarRow = ({ label, value, max }: { label: string; value: number; max: number }) => {
  const widthPct = max <= 0 ? 0 : Math.min(100, (Math.abs(value) / max) * 100);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-foreground">{formatMoney(value)}</span>
      </div>
      <progress
        className="h-2 w-full overflow-hidden rounded bg-muted [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
        max={100}
        value={widthPct}
      />
    </div>
  );
};

export default function ProjectAccounting() {
  const { projectId } = useParams<{ projectId: string }>();
  const { state, reload } = useApp();

  const project = useMemo<Project | null>(() => {
    const projects = state?.projects ?? [];
    return projects.find((p) => String(p.id).toLowerCase() === String(projectId).toLowerCase()) || null;
  }, [projectId, state?.projects]);

  const currentUser = state?.currentUser ?? null;
  const projectOrg = useMemo(() => {
    if (!project) return null;
    const organizations = state?.organizations ?? [];
    return organizations.find((org) => String(org.id).toLowerCase() === String(project.organizationId).toLowerCase()) || null;
  }, [project, state?.organizations]);

  const canView = canViewProjectAccountingUi(currentUser, project, projectOrg);
  const canEdit = canEditProjectAccountingUi(currentUser, project, projectOrg);
  const moduleEnabled = !!(project?.accountingModules?.sr || project?.accountingModules?.fcm);

  const [requirements, setRequirements] = useState<AccountingRequirement[]>(
    project?.accountingRequirements || []
  );
  const [milestones, setMilestones] = useState<AccountingMilestone[]>(
    project?.accountingMilestones || []
  );
  const [entries, setEntries] = useState<CapitalLedgerEntry[]>(
    project?.capitalLedgerEntries || []
  );
  const [asOfDate, setAsOfDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [finalRevenueOverride, setFinalRevenueOverride] = useState<string>('');

  if (!project) {
    return <div className="p-6 text-muted-foreground">Project not found.</div>;
  }

  if (!canView) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Access Restricted</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Accounting UI is restricted to assigned finance roles.
            </p>
            <Link to={`/app/projects/${project.id}`} className="text-primary hover:underline">
              Back to project overview
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!moduleEnabled) {
    return (
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Accounting Module Not Enabled</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              SR/FCM accounting is optional per project and currently disabled.
            </p>
            {canEdit ? (
              <Link to={`/app/projects/${project.id}/settings`} className="text-primary hover:underline">
                Enable modules in Project Settings
              </Link>
            ) : (
              <p className="text-xs text-muted-foreground">Ask a project owner/admin to enable it.</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const snapshot = computeUnifiedAccountingSnapshot(
    entries,
    milestones,
    asOfDate,
    finalRevenueOverride ? parseFloat(finalRevenueOverride) || 0 : undefined
  );

  const report = createUnifiedAccountingReportTemplate(
    entries,
    milestones,
    asOfDate,
    finalRevenueOverride ? parseFloat(finalRevenueOverride) || 0 : undefined
  );

  const validation = useMemo(
    () => validateUnifiedAccountingInputs(requirements, milestones, entries, project.accountingModules),
    [entries, milestones, project.accountingModules, requirements]
  );

  const milestoneDrilldown = useMemo(() => {
    return milestones.map((ms) => ({
      id: ms.id,
      code: ms.code,
      title: ms.title,
      module: ms.module,
      budget: ms.budget || 0,
      investment: ms.investment || 0,
      actualCost: ms.actualCost || 0,
      taxes: ms.taxes || 0,
      finalRevenue: ms.finalRevenue || 0,
      profit: (ms.finalRevenue || 0) - (ms.actualCost || 0) - (ms.taxes || 0),
    }));
  }, [milestones]);

  const drilldownMax = useMemo(() => {
    const values = milestoneDrilldown.flatMap((row) => [
      Math.abs(row.budget),
      Math.abs(row.investment),
      Math.abs(row.actualCost),
      Math.abs(row.taxes),
      Math.abs(row.finalRevenue),
      Math.abs(row.profit),
    ]);
    return Math.max(1, ...values);
  }, [milestoneDrilldown]);

  const handleExportCsv = () => {
    const csv = buildUnifiedAccountingCsv(report);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${project.id}-accounting-${report.asOfDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintPdfStyle = () => {
    const printWindow = window.open('', '_blank', 'width=1000,height=800');
    if (!printWindow) {
      toast.error('Unable to open print window');
      return;
    }

    const html = `
      <html>
        <head>
          <title>Unified Accounting Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111827; }
            h1, h2 { margin: 0 0 12px 0; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 18px; }
            th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; font-size: 12px; }
            th { background: #f3f4f6; }
            .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
            .card { border: 1px solid #d1d5db; border-radius: 8px; padding: 12px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Unified Accounting Report</h1>
          <p>Project: ${project.name}</p>
          <p>As of: ${report.asOfDate}</p>
          <div class="grid">
            <div class="card"><strong>Total Principal</strong><br/>${formatMoney(report.summary.totalPrincipal)}</div>
            <div class="card"><strong>Total Accrued Interest</strong><br/>${formatMoney(report.summary.totalAccruedInterest)}</div>
            <div class="card"><strong>Net Investor Profit</strong><br/>${formatMoney(report.summary.netInvestorProfit)}</div>
            <div class="card"><strong>Total Value</strong><br/>${formatMoney(report.summary.totalValue)}</div>
          </div>
          <h2>Milestone Financials</h2>
          <table>
            <thead>
              <tr><th>Code</th><th>Title</th><th>Module</th><th>Budget</th><th>Investment</th><th>Actual Cost</th><th>Taxes</th><th>Final Revenue</th></tr>
            </thead>
            <tbody>
              ${report.sections.milestoneFinancials.map((m) => `<tr><td>${m.code}</td><td>${m.title}</td><td>${m.module}</td><td>${formatMoney(m.budget)}</td><td>${formatMoney(m.investment)}</td><td>${formatMoney(m.actualCost)}</td><td>${formatMoney(m.taxes)}</td><td>${formatMoney(m.finalRevenue)}</td></tr>`).join('')}
            </tbody>
          </table>
          <h2>Ledger Rows</h2>
          <table>
            <thead>
              <tr><th>Module</th><th>Requirement</th><th>Milestone</th><th>Principal</th><th>Rate</th><th>Days</th><th>Accrued Interest</th><th>Note</th></tr>
            </thead>
            <tbody>
              ${report.sections.ledgerRows.map((r) => `<tr><td>${r.module}</td><td>${r.requirementId || '-'}</td><td>${r.milestoneId || '-'}</td><td>${formatMoney(r.principal)}</td><td>${(r.annualInterestRate * 100).toFixed(2)}%</td><td>${r.daysInInvest}</td><td>${formatMoney(r.accruedInterest)}</td><td>${r.note}</td></tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const addRequirement = () => {
    const now = new Date().toISOString();
    const module: AccountingModule = project.accountingModules?.sr ? 'SR' : 'FCM';
    setRequirements((prev) => [
      ...prev,
      {
        id: generateId('acct-req'),
        organizationId: project.organizationId,
        projectId: project.id,
        module,
        code: `${module}-REQ-${String(prev.length + 1).padStart(3, '0')}`,
        title: 'New requirement',
        description: '',
        milestoneIds: [],
        budget: 0,
        investment: 0,
        actualCost: 0,
        taxes: 0,
        status: 'Draft',
        createdAt: now,
        updatedAt: now,
        createdBy: currentUser?.id,
        createdByName: currentUser?.name,
      },
    ]);
  };

  const addMilestone = () => {
    const now = new Date().toISOString();
    const module: AccountingModule = project.accountingModules?.sr ? 'SR' : 'FCM';
    setMilestones((prev) => [
      ...prev,
      {
        id: generateId('acct-ms'),
        organizationId: project.organizationId,
        projectId: project.id,
        module,
        code: `M-${module}-${String(prev.length + 1).padStart(2, '0')}`,
        title: 'New milestone',
        description: '',
        requirementIds: [],
        budget: 0,
        investment: 0,
        actualCost: 0,
        taxes: 0,
        finalRevenue: 0,
        status: 'Planned',
        createdAt: now,
        updatedAt: now,
      },
    ]);
  };

  const addLedgerEntry = () => {
    const module: AccountingModule = project.accountingModules?.sr ? 'SR' : 'FCM';
    setEntries((prev) => [
      ...prev,
      {
        id: generateId('capital-ledger'),
        organizationId: project.organizationId,
        projectId: project.id,
        module,
        startDate: new Date().toISOString().slice(0, 10),
        amount: 0,
        note: 'New transaction',
        annualInterestRate: 0.1,
        createdAt: new Date().toISOString(),
        createdBy: currentUser?.id,
      },
    ]);
  };

  const seedReferenceData = () => {
    const module: AccountingModule = project.accountingModules?.sr ? 'SR' : 'FCM';
    const now = new Date().toISOString();
    const seeded = REFERENCE_LEDGER_TRANSACTIONS.map((item, index) => ({
      id: generateId('capital-ref', index),
      organizationId: project.organizationId,
      projectId: project.id,
      module,
      startDate: item.startDate,
      endDate: item.endDate,
      amount: item.amount,
      note: item.note,
      annualInterestRate: item.annualInterestRate,
      createdAt: now,
      createdBy: currentUser?.id,
    }));

    setEntries(seeded);
    toast.success('Reference ledger transactions loaded');
  };

  const saveAll = async () => {
    if (!canEdit) {
      toast.error('Only project owners/admins can edit accounting data.');
      return;
    }

    if (!validation.isValid) {
      toast.error(`Validation failed: ${validation.errors[0]}`);
      return;
    }

    try {
      await Promise.all([
        setProjectAccountingRequirements(project.organizationId, project.id, requirements),
        setProjectAccountingMilestones(project.organizationId, project.id, milestones),
        setProjectCapitalLedgerEntries(project.organizationId, project.id, entries),
      ]);

      toast.success('Accounting data saved');
      const email = currentUser?.email;
      if (email) {
        await reload(email);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save accounting data');
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl flex items-center gap-2">
            <Calculator className="w-7 h-7" />
            Unified Accounting
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tracks SR/FCM requirements, milestones, and capital ledger with time-value of money.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportCsv} className="gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={handlePrintPdfStyle} className="gap-2">
            <Printer className="w-4 h-4" />
            Print/PDF
          </Button>
          {canEdit && (
            <Button onClick={saveAll} className="gap-2" disabled={!validation.isValid}>
              <Save className="w-4 h-4" />
              Save
            </Button>
          )}
        </div>
      </div>

      {(validation.errors.length > 0 || validation.warnings.length > 0) && (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-4 h-4" />
              Validation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {validation.errors.map((error, idx) => (
              <p key={`err-${idx}`} className="text-destructive">• {error}</p>
            ))}
            {validation.warnings.map((warning, idx) => (
              <p key={`warn-${idx}`} className="text-muted-foreground">• {warning}</p>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Principal</p><p className="text-lg">${snapshot.summary.totalPrincipal.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Accrued Interest</p><p className="text-lg">${snapshot.summary.totalAccruedInterest.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Net Investor Profit (P)</p><p className="text-lg">${snapshot.summary.netInvestorProfit.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Snapshot Total Value</p><p className="text-lg">${snapshot.summary.totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Snapshot Controls</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="asOfDate">As-of Date</Label>
            <Input id="asOfDate" type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="finalRevenue">Final Milestone Revenue (R_e) Override</Label>
            <Input
              id="finalRevenue"
              type="number"
              value={finalRevenueOverride}
              onChange={(e) => setFinalRevenueOverride(e.target.value)}
              placeholder="Optional override"
            />
          </div>
          <div className="text-xs text-muted-foreground self-end">
            Formula: P = R_e - C - T
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Requirements (R)</span>
            {canEdit && <Button size="sm" onClick={addRequirement}><Plus className="w-4 h-4 mr-1" />Add</Button>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {requirements.length === 0 ? (
            <p className="text-sm text-muted-foreground">No accounting requirements yet.</p>
          ) : (
            requirements.map((req, index) => (
              <div key={req.id} className="grid grid-cols-1 md:grid-cols-6 gap-2 rounded border p-3">
                <Input value={req.code} disabled={!canEdit} onChange={(e) => setRequirements((prev) => prev.map((it, i) => i === index ? { ...it, code: e.target.value, updatedAt: new Date().toISOString() } : it))} />
                <Input value={req.title} disabled={!canEdit} onChange={(e) => setRequirements((prev) => prev.map((it, i) => i === index ? { ...it, title: e.target.value, updatedAt: new Date().toISOString() } : it))} />
                <select aria-label="Requirement module" value={req.module} disabled={!canEdit} onChange={(e) => setRequirements((prev) => prev.map((it, i) => i === index ? { ...it, module: e.target.value as AccountingModule, updatedAt: new Date().toISOString() } : it))} className="rounded-md border border-border bg-background px-3">
                  {moduleOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <Input type="number" value={req.budget || 0} disabled={!canEdit} onChange={(e) => setRequirements((prev) => prev.map((it, i) => i === index ? { ...it, budget: parseFloat(e.target.value) || 0, updatedAt: new Date().toISOString() } : it))} />
                <Input type="number" value={req.actualCost || 0} disabled={!canEdit} onChange={(e) => setRequirements((prev) => prev.map((it, i) => i === index ? { ...it, actualCost: parseFloat(e.target.value) || 0, updatedAt: new Date().toISOString() } : it))} />
                <select aria-label="Requirement status" value={req.status} disabled={!canEdit} onChange={(e) => setRequirements((prev) => prev.map((it, i) => i === index ? { ...it, status: e.target.value as AccountingRequirement['status'], updatedAt: new Date().toISOString() } : it))} className="rounded-md border border-border bg-background px-3">
                  <option value="Draft">Draft</option>
                  <option value="Approved">Approved</option>
                  <option value="Implemented">Implemented</option>
                </select>
                <div className="md:col-span-6">
                  <Textarea value={req.description || ''} disabled={!canEdit} onChange={(e) => setRequirements((prev) => prev.map((it, i) => i === index ? { ...it, description: e.target.value, updatedAt: new Date().toISOString() } : it))} placeholder="Requirement description" rows={2} />
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Milestones (M)</span>
            {canEdit && <Button size="sm" onClick={addMilestone}><Plus className="w-4 h-4 mr-1" />Add</Button>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {milestones.length === 0 ? (
            <p className="text-sm text-muted-foreground">No accounting milestones yet.</p>
          ) : (
            milestones.map((ms, index) => (
              <div key={ms.id} className="grid grid-cols-1 md:grid-cols-7 gap-2 rounded border p-3">
                <Input value={ms.code} disabled={!canEdit} onChange={(e) => setMilestones((prev) => prev.map((it, i) => i === index ? { ...it, code: e.target.value, updatedAt: new Date().toISOString() } : it))} />
                <Input value={ms.title} disabled={!canEdit} onChange={(e) => setMilestones((prev) => prev.map((it, i) => i === index ? { ...it, title: e.target.value, updatedAt: new Date().toISOString() } : it))} />
                <select aria-label="Milestone module" value={ms.module} disabled={!canEdit} onChange={(e) => setMilestones((prev) => prev.map((it, i) => i === index ? { ...it, module: e.target.value as AccountingModule, updatedAt: new Date().toISOString() } : it))} className="rounded-md border border-border bg-background px-3">
                  {moduleOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.value}</option>)}
                </select>
                <Input type="number" value={ms.budget || 0} disabled={!canEdit} onChange={(e) => setMilestones((prev) => prev.map((it, i) => i === index ? { ...it, budget: parseFloat(e.target.value) || 0, updatedAt: new Date().toISOString() } : it))} />
                <Input type="number" value={ms.actualCost || 0} disabled={!canEdit} onChange={(e) => setMilestones((prev) => prev.map((it, i) => i === index ? { ...it, actualCost: parseFloat(e.target.value) || 0, updatedAt: new Date().toISOString() } : it))} />
                <Input type="number" value={ms.taxes || 0} disabled={!canEdit} onChange={(e) => setMilestones((prev) => prev.map((it, i) => i === index ? { ...it, taxes: parseFloat(e.target.value) || 0, updatedAt: new Date().toISOString() } : it))} />
                <Input type="number" value={ms.finalRevenue || 0} disabled={!canEdit} onChange={(e) => setMilestones((prev) => prev.map((it, i) => i === index ? { ...it, finalRevenue: parseFloat(e.target.value) || 0, updatedAt: new Date().toISOString() } : it))} />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><DollarSign className="w-4 h-4" />Capital Ledger</span>
            <div className="flex items-center gap-2">
              {canEdit && <Button size="sm" variant="outline" onClick={seedReferenceData}>Load Reference Data</Button>}
              {canEdit && <Button size="sm" onClick={addLedgerEntry}><Plus className="w-4 h-4 mr-1" />Add</Button>}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No capital ledger entries yet.</p>
          ) : (
            entries.map((entry, index) => (
              <div key={entry.id} className="grid grid-cols-1 md:grid-cols-8 gap-2 rounded border p-3">
                <Input type="date" value={toDateValue(entry.startDate)} disabled={!canEdit} onChange={(e) => setEntries((prev) => prev.map((it, i) => i === index ? { ...it, startDate: e.target.value } : it))} />
                <Input type="date" value={toDateValue(entry.endDate)} disabled={!canEdit} onChange={(e) => setEntries((prev) => prev.map((it, i) => i === index ? { ...it, endDate: e.target.value || undefined } : it))} />
                <Input type="number" value={entry.amount} disabled={!canEdit} onChange={(e) => setEntries((prev) => prev.map((it, i) => i === index ? { ...it, amount: parseFloat(e.target.value) || 0 } : it))} />
                <Input type="number" step="0.001" value={entry.annualInterestRate} disabled={!canEdit} onChange={(e) => setEntries((prev) => prev.map((it, i) => i === index ? { ...it, annualInterestRate: parseFloat(e.target.value) || 0 } : it))} />
                <select aria-label="Ledger module" value={entry.module} disabled={!canEdit} onChange={(e) => setEntries((prev) => prev.map((it, i) => i === index ? { ...it, module: e.target.value as AccountingModule } : it))} className="rounded-md border border-border bg-background px-3">
                  {moduleOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.value}</option>)}
                </select>
                <select aria-label="Ledger requirement tag" value={entry.requirementId || ''} disabled={!canEdit} onChange={(e) => setEntries((prev) => prev.map((it, i) => i === index ? { ...it, requirementId: e.target.value || undefined } : it))} className="rounded-md border border-border bg-background px-3">
                  <option value="">Requirement</option>
                  {requirements.map((req) => <option key={req.id} value={req.id}>{req.code}</option>)}
                </select>
                <select aria-label="Ledger milestone tag" value={entry.milestoneId || ''} disabled={!canEdit} onChange={(e) => setEntries((prev) => prev.map((it, i) => i === index ? { ...it, milestoneId: e.target.value || undefined } : it))} className="rounded-md border border-border bg-background px-3">
                  <option value="">Milestone</option>
                  {milestones.map((ms) => <option key={ms.id} value={ms.id}>{ms.code}</option>)}
                </select>
                <Input value={entry.note} disabled={!canEdit} onChange={(e) => setEntries((prev) => prev.map((it, i) => i === index ? { ...it, note: e.target.value } : it))} />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Milestone Drilldown Charts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {milestoneDrilldown.length === 0 ? (
            <p className="text-sm text-muted-foreground">No milestones to chart.</p>
          ) : (
            milestoneDrilldown.map((item) => (
              <div key={item.id} className="rounded-lg border border-border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">{item.code} - {item.title}</h3>
                  <span className="text-xs text-muted-foreground">{item.module}</span>
                </div>
                <BarRow label="Budget" value={item.budget} max={drilldownMax} />
                <BarRow label="Investment" value={item.investment} max={drilldownMax} />
                <BarRow label="Actual Cost" value={item.actualCost} max={drilldownMax} />
                <BarRow label="Taxes" value={item.taxes} max={drilldownMax} />
                <BarRow label="Final Revenue" value={item.finalRevenue} max={drilldownMax} />
                <BarRow label="Profit" value={item.profit} max={drilldownMax} />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reporting Template (Snapshot)</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="whitespace-pre-wrap rounded bg-muted p-3 text-xs text-foreground overflow-auto max-h-[360px]">
{JSON.stringify(report, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
